import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from 'api';
import { NotificationsCountType } from '../Notifications.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

const NOTIFICATIONS_COUNT_QUERY_KEY = 'notifications-count';

type UseNotificationsCountQueryParams = {
  enabled?: boolean;
};

export default function useNotificationsCountQuery({
  enabled = true,
}: UseNotificationsCountQueryParams) {
  const queryClient = useQueryClient();

  const getMixpanelProps = useGetMixpanelProps();

  const updateNotificationsCount = useCallback(
    (updater: (old: NotificationsCountType) => NotificationsCountType) => {
      queryClient.setQueriesData<NotificationsCountType>(
        { queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;
          return updater(oldData);
        },
      );
    },
    [queryClient],
  );

  const query = useQuery<NotificationsCountType>({
    queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.notifications.getUnreadNotifications();

      return {
        all: data.allNotificationsCount,
        unread: data.unreadNotificationsCount,
      };
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled,
  });

  return { query, updateNotificationsCount };
}
